import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { formatEventDateGmt } from '../utils/eventDate';

const Search = () => {
  const [searchParams] = useSearchParams();
  const q = String(searchParams.get('q') || '').trim();
  const [loading, setLoading] = useState(Boolean(q));
  const [data, setData] = useState({ cups: [], matches: [], polls: [], total: 0 });

  useEffect(() => {
    let cancelled = false;
    if (!q || q.length < 2) {
      setData({ cups: [], matches: [], polls: [], total: 0 });
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    (async () => {
      try {
        const { data: res } = await api.get('/search', { params: { q, limit: 12 } });
        if (!cancelled) setData(res || { cups: [], matches: [], polls: [], total: 0 });
      } catch {
        if (!cancelled) setData({ cups: [], matches: [], polls: [], total: 0 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [q]);

  const empty = useMemo(
    () => !loading && q.length >= 2 && !(data.total > 0),
    [loading, q, data.total]
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Search</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {q ? (
            <>
              Results for <span className="font-semibold text-gray-800 dark:text-gray-200">“{q}”</span>
            </>
          ) : (
            'Type a team, poll, or tournament in the navbar.'
          )}
        </p>

        {q && q.length < 2 ? (
          <p className="mt-8 text-sm text-gray-500">Enter at least 2 characters to search.</p>
        ) : null}

        {loading ? (
          <div className="py-16 flex justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-red-600 border-t-transparent" />
          </div>
        ) : null}

        {empty ? (
          <div className="mt-10 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-6 py-12 text-center">
            <p className="font-semibold text-gray-900 dark:text-white">No results</p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Try a team name, poll question, or tournament.
            </p>
          </div>
        ) : null}

        {!loading && data.cups?.length > 0 ? (
          <section className="mt-8">
            <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
              Tournaments
            </h2>
            <div className="space-y-2">
              {data.cups.map((cup) => (
                <Link
                  key={cup._id}
                  to={`/cup/${cup.slug}`}
                  className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 py-3 hover:border-red-400 dark:hover:border-red-700 transition-colors"
                >
                  {cup.thumbnailImage ? (
                    <img src={cup.thumbnailImage} alt="" className="w-10 h-10 rounded-lg object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-stone-100 dark:bg-stone-800 flex items-center justify-center text-red-600 text-xs font-bold">
                      {(cup.name || '?').slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 dark:text-white truncate">{cup.name}</div>
                    {cup.status ? (
                      <div className="text-xs text-gray-500 capitalize">{cup.status}</div>
                    ) : null}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ) : null}

        {!loading && data.matches?.length > 0 ? (
          <section className="mt-8">
            <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
              Matches
            </h2>
            <div className="space-y-2">
              {data.matches.map((m) => (
                <Link
                  key={m._id}
                  to={`/match/${m._id}/free`}
                  className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 py-3 hover:border-red-400 dark:hover:border-red-700 transition-colors"
                >
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 dark:text-white truncate">
                      {m.teamA} vs {m.teamB}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {[m.cupName, m.stageName, formatEventDateGmt(m.date)].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-gray-500">
                    {m.status || '—'}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        ) : null}

        {!loading && data.polls?.length > 0 ? (
          <section className="mt-8">
            <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
              Polls
            </h2>
            <div className="space-y-2">
              {data.polls.map((p) => (
                <Link
                  key={p._id}
                  to={`/poll/${p._id}/free`}
                  className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 py-3 hover:border-red-400 dark:hover:border-red-700 transition-colors"
                >
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 dark:text-white truncate">{p.question}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {[p.cupName, formatEventDateGmt(p.date)].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-gray-500">
                    {p.status || '—'}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
};

export default Search;
